import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Flex, Text, Card, Button, Badge, Tooltip } from "@radix-ui/themes";
import { useGetAllBrands, useGetWirelessLAN } from "hooks";
import { useProfileContext, useLocationContext } from "context";
import { LockClosedIcon, InfoCircledIcon } from "@radix-ui/react-icons";

const NetworksSelector = () => {
  const navigate = useNavigate();
  const { userProfile } = useProfileContext();
  const { locationsData } = useLocationContext();
  const organisationID = userProfile.organisation_id;

  const navigateToOrganisationLocations = () => {
    navigate("/organisation/locations");
  };

  const navigateToOrganisationLocationsWithBrandModal = () => {
    navigate("/organisation/locations", {
      state: { openEditBrands: true },
      replace: true,
    });
  };

  // Fetch brands
  const { data: brandsData, isLoading: isLoadingBrands, error: brandsError } = useGetAllBrands(organisationID);

  // Fetch all wireless LAN data
  const { data: wirelessLanData, isLoading: isLoadingWirelessLan } = useGetWirelessLAN(organisationID);

  // State to hold filtered data
  const [brandsWithLocations, setBrandsWithLocations] = useState([]);
  const [independentLocations, setIndependentLocations] = useState([]);
  const [defaultBrand, setDefaultBrand] = useState(null);
  const [networksMap, setNetworksMap] = useState({});

  // Process brands and locations data
  useEffect(() => {
    if (brandsData && locationsData) {
      // Find the default brand
      const defaultBrandItem = brandsData.find((brand) => brand.is_default);
      setDefaultBrand(defaultBrandItem);

      // Get non-default brands with their associated locations
      const nonDefaultBrands = brandsData.filter((brand) => !brand.is_default);

      // Add location counts to each brand
      const enhancedBrands = nonDefaultBrands.map((brand) => {
        const locationsInBrand = locationsData.filter((location) => location.brand_id === brand.id);

        return {
          ...brand,
          locations: locationsInBrand,
          locationCount: locationsInBrand.length,
        };
      });

      setBrandsWithLocations(enhancedBrands);

      // Filter locations that belong to the default brand only
      const defaultBrandLocations = locationsData.filter((location) => location.brand_id === defaultBrandItem?.id);

      setIndependentLocations(defaultBrandLocations);
    }
  }, [brandsData, locationsData]);

  // Process wireless LAN data to map networks to locations and brands
  useEffect(() => {
    if (wirelessLanData?.data) {
      const networksByLocationAndBrand = {};

      // Group networks by location_id and brand_id
      wirelessLanData.data.forEach((network) => {
        // Create key for location
        const locationKey = `location_${network.location_id}`;
        if (!networksByLocationAndBrand[locationKey]) {
          networksByLocationAndBrand[locationKey] = [];
        }
        networksByLocationAndBrand[locationKey].push(network);

        // Create key for brand
        const brandKey = `brand_${network.brand_id}`;
        if (!networksByLocationAndBrand[brandKey]) {
          networksByLocationAndBrand[brandKey] = [];
        }
        networksByLocationAndBrand[brandKey].push(network);
      });

      setNetworksMap(networksByLocationAndBrand);
    }
  }, [wirelessLanData]);

  // Get networks for a specific location or brand
  const getNetworksFor = (type, id) => {
    const key = `${type}_${id}`;
    const networks = networksMap[key] || [];

    // Group by network type
    const guestNetwork = networks.find((n) => n.type.includes("Guest"));
    const workNetwork = networks.find((n) => n.type.includes("Work"));
    const staffNetwork = networks.find((n) => n.type.includes("Staff"));

    return { guestNetwork, workNetwork, staffNetwork };
  };

  // Network Badge component
  const NetworkBadge = ({ network, label, showLock = false }) => {
    const isActive = network?.enabled;

    return (
      <Flex align="center" gap="0" pt="3">
        <Badge size="1" variant="soft" 
          color={isActive ? "blue" : "gray"}
          style={{ borderTopRightRadius: 0, borderBottomRightRadius: 0 }}>
          {showLock && <LockClosedIcon width="12" height="12" />} {label}
        </Badge>
        <Badge size="1" variant="solid"
          color={isActive ? "blue" : "gray"}
          style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}>
          {network?.name || "Not set"}
        </Badge>
      </Flex>
    );
  };

  // Handle brand click
  const handleBrandClick = (brand) => {
    // Find the first location in this brand
    const firstLocation = locationsData?.find((location) => location.brand_id === brand.id);

    navigate(`/networks`, {
      state: {
        isBrand: true,
        brandId: brand.id,
        brandName: brand.name,
        ...(firstLocation && { firstLocationId: firstLocation.id }),
      },
    });
  };

  // Handle independent location click
  const handleLocationClick = (location) => {
    navigate(`/networks`, {
      state: {
        isBrand: false,
        locationName: location.name,
        locationId: location.id,
      },
    });
  };

  // Simple loading state
  if (isLoadingBrands || !locationsData || isLoadingWirelessLan) {
    return (
      <Flex align="center" justify="center" style={{ minHeight: "400px" }}>
        <Text>Loading network settings...</Text>
      </Flex>
    );
  }

  // Simple error state
  if (brandsError) {
    return (
      <Flex align="center" justify="center" style={{ minHeight: "400px" }}>
        <Text color="red">Error loading network information</Text>
      </Flex>
    );
  }

  const hasCustomBrands = brandsWithLocations.length > 0;

  return (
    <Flex direction="column" gap="5" p="5" style={{ width: "100%" }}>
      
      { hasCustomBrands && (
        <Flex direction="column" gap="4">
          {/* Brands header section */}
          <Flex justify="between" align="center">
            <Text size="3" weight="medium">Brands</Text>
            <Button size="1" variant="soft" color="gray" 
              onClick={navigateToOrganisationLocationsWithBrandModal}>
              Edit brands
            </Button>
          </Flex>
          {/* Brands settings section */}
          <Flex direction="column" gap="4">
            {brandsWithLocations.map((brand) => {
              const { guestNetwork, workNetwork, staffNetwork } = getNetworksFor("brand", brand.id);
              return (
                <Card key={brand.id} asChild>
                  <button
                    onClick={() => handleBrandClick(brand)}
                    style={{ width: "100%", textAlign: "left", height: "100%", padding: 'var(--space-4)' }}>
                    <Flex justify="between" align="start" gap="4">
                      {/* Column 1: Brand name and location count */}
                      <Flex direction="column" style={{ width: "25%", color: 'var(--gray-12)' }} gap="1">
                        <Text weight="medium" size="2">
                          {brand.name}
                        </Text>
                        <Text size="1">
                          {brand.locationCount || 0} {brand.locationCount === 1 ? "location" : "locations"}
                        </Text>
                      </Flex>

                      {/* Column 2: Guest network */}
                      <Flex direction="column" style={{ width: "25%" }}>
                        <NetworkBadge network={guestNetwork} label="Guest" />
                      </Flex>

                      {/* Column 3: Work network */}
                      <Flex direction="column" style={{ width: "25%" }}>
                        <NetworkBadge network={workNetwork} label="Work" showLock={true} />
                      </Flex>

                      {/* Column 4: Staff network */}
                      <Flex direction="column" style={{ width: "25%" }}>
                        <NetworkBadge network={staffNetwork} label="Staff" showLock={true} />
                      </Flex>
                    </Flex>
                  </button>
                </Card>
              );
            })}
          </Flex>
        </Flex>
        
      )}
      
      { independentLocations.length > 0 && (
        <Flex direction="column" gap="4">
          {/* Locations header */}
          <Flex justify="between" align="center">
            <Flex align="center" gap="2">
              <Text size="3" weight="medium">Locations</Text>
              <Tooltip maxWidth="202px" style={{ textAlign: "center"}}
                content="Independently managed locations, not assigned to any brand.">
                <InfoCircledIcon/>
              </Tooltip>
            </Flex>
            <Button size="1" variant="soft" color="gray" 
              onClick={navigateToOrganisationLocations}>
              Edit locations
            </Button>
            </Flex>
            {independentLocations.map((location) => {
            const { guestNetwork, workNetwork, staffNetwork } = getNetworksFor("location", location.id);
            return (
              <Card key={location.id} asChild>
                <button
                  onClick={() => handleLocationClick(location)}
                  style={{ width: "100%", height: "100%", padding: 'var(--space-4)' }}>
                  <Flex justify="between" align="start" gap="4">
                    {/* Column 1: Location name and address */}
                    <Flex direction="column" style={{ width: "25%" }} gap="1">
                      <Text weight="medium" size="2" style={{ color: 'var(--gray-12)'}}>
                        {location.name}
                      </Text>
                      <Text size="1"
                        style={{ textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap" }}>
                        {location.address || "No address"}
                      </Text>
                    </Flex>

                    {/* Column 2: Guest network */}
                    <Flex direction="column" style={{ width: "25%" }}>
                      <NetworkBadge network={guestNetwork} label="Guest" />
                    </Flex>

                    {/* Column 3: Work network */}
                    <Flex direction="column" style={{ width: "25%" }}>
                      <NetworkBadge network={workNetwork} label="Work" showLock={true} />
                    </Flex>

                    {/* Column 4: Staff network */}
                    <Flex direction="column" style={{ width: "25%" }}>
                      <NetworkBadge network={staffNetwork} label="Staff" showLock={true} />
                    </Flex>
                  </Flex>
                </button>
              </Card>
            );
          })}
        </Flex>
      )}

      { ( !hasCustomBrands && independentLocations.length === 0) ? (
        // No brands and no locations
        <Flex direction="column" gap="5" style={{ width: "100%" }}>
          {/* No brands section */}
          <Flex direction="column" gap="4">
            <Flex style={{ justifyContent: 'space-between', alignItems: 'center', alignSelf: 'stretch' }}>
              <Text size="3" weight="medium">Brands</Text>
            </Flex>
            <Flex direction="column" gap="5" style={{ alignItems: "center", justifyContent: "center", width: "100%", 
              borderRadius: "var(--space-2)", border: "1px dashed var(--gray-6)", minHeight: "200px" }}>
              <Flex direction="column" gap="2" style={{ alignItems: "center", maxWidth: "296px" }}>
                <Text size="2" weight="medium">No brands</Text>
                <Text size="1" color="gray" align="center">
                  Create brands to easily manage captive portals and networks across your locations.
                </Text>
              </Flex>
              <Button size="2" variant="soft" onClick={navigateToOrganisationLocationsWithBrandModal}>
                Manage brands
              </Button>
            </Flex>
          </Flex>
          {/* No locations section */}
          <Flex direction="column" gap="4">
            <Flex gap="2" style={{ alignItems: 'center', alignSelf: 'stretch', justifyContent: 'flex-start' }}>
              <Text size="3" weight="medium">Locations</Text>
              <Tooltip maxWidth="202px" style={{ textAlign: "center"}}
                content="Independently managed locations, not assigned to any brand.">
                <InfoCircledIcon/>
              </Tooltip>
            </Flex>
            <Flex direction="column" gap="5"
              style={{ alignItems: "center", justifyContent: "center", width: "100%", 
                borderRadius: "var(--space-2)", border: "1px dashed var(--gray-6)", minHeight: "200px" }}>
              <Flex direction="column" gap="2" style={{ alignItems: "center", maxWidth: '296px' }}>
                <Text size="2" weight="medium">No independent locations</Text>
                <Text size="1" color="gray" align="center">
                  Create your first location to start setting up your captive portals and networks.
                </Text>
              </Flex>
              <Button size="2" variant="soft" onClick={navigateToOrganisationLocations}>
                Manage locations
              </Button>
            </Flex>
          </Flex>
        </Flex>
      ) : (
        ( hasCustomBrands && independentLocations.length === 0) ? (
          <Flex direction="column" gap="4">
            <Flex gap="2" style={{ alignItems: 'center', alignSelf: 'stretch', justifyContent: 'flex-start' }}>
              <Text size="3" weight="medium">Locations</Text>
              <Tooltip maxWidth="202px" style={{ textAlign: "center"}}
                content="Independently managed locations, not assigned to any brand.">
                <InfoCircledIcon/>
              </Tooltip>
            </Flex>
            <Flex direction="column" gap="5"
              style={{ alignItems: "center", justifyContent: "center", width: "100%", 
                borderRadius: "var(--space-2)", border: "1px dashed var(--gray-6)", minHeight: "200px" }}>
              <Flex direction="column" gap="2" style={{ alignItems: "center", maxWidth: '296px' }}>
                <Text size="2" weight="medium">No independent locations</Text>
                <Text size="1" color="gray" align="center">
                  Create your first location to start setting up your captive portals and networks.
                </Text>
              </Flex>
              <Button size="2" variant="soft" onClick={navigateToOrganisationLocations}>
                Manage locations
              </Button>
            </Flex>
          </Flex>
        ) : (
          independentLocations.length > 0 && !hasCustomBrands && (
            <Flex direction="column" gap="4">
            <Flex style={{ justifyContent: 'space-between', alignItems: 'center', alignSelf: 'stretch' }}>
              <Text size="3" weight="medium">Brands</Text>
            </Flex>
            <Flex direction="column" gap="5" style={{ alignItems: "center", justifyContent: "center", width: "100%", 
              borderRadius: "var(--space-2)", border: "1px dashed var(--gray-6)", minHeight: "200px" }}>
              <Flex direction="column" gap="2" style={{ alignItems: "center", maxWidth: "296px" }}>
                <Text size="2" weight="medium">
                  No brands
                </Text>
                <Text size="1" color="gray" align="center">
                  Create brands to easily manage captive portals and networks across your locations.
                </Text>
              </Flex>
              <Button size="2" variant="soft" onClick={navigateToOrganisationLocationsWithBrandModal}>
                Manage brands
              </Button>
            </Flex>
          </Flex>
        )
      ))}
    </Flex>
  );
};

export default NetworksSelector;
